<template>
  <authenticated-layout
    :back-route="{ name: 'tenants' }"
    :breadcrumbs="breadcrumbItems">

    <PageHeader :title="tenant?.title">
      <template #actions>
        <v-menu bottom transition="scale-transition">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="outlined"
              color="primary"
              prepend-icon="fa fa-cog"
              data-pw="menuButton"
            >
              Settings
            </v-btn>
          </template>
          <v-list min-width="250px">
            <v-list-item
              @click="showCreateInstitution = true"
              data-pw="createInstitutionButton"
            >
              {{ $t("components.TenantDetailPage.menu.create_institution") }}
            </v-list-item>
            <v-list-item @click="showDisableEnableTenant = true">
              <span v-show="tenant?.disabled" style="color:green;">
                {{ $t('components.TenantDetailPage.menu.enable') }}
              </span>
              <span v-show="!tenant?.disabled" style="color:red;">
                {{ $t('components.TenantDetailPage.menu.disable') }}
              </span>
            </v-list-item>
            <v-list-item @click="showDeleteTenant = true">
              <span style="color:red;">{{ $t("components.TenantDetailPage.menu.delete") }}</span>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </PageHeader>
    <!--    <v-alert-->
    <!--      v-if="tenant?.disabled"-->
    <!--      type="warning"-->
    <!--      density="compact"-->
    <!--      class="my-4"-->
    <!--    >-->
    <!--      {{ $t("components.TenantDetailPage.content.this_tenant_disabled") }}-->
    <!--    </v-alert>-->

    <v-container class="mt-0 mx-auto pa-0 border-b mb-6" fluid>
      <v-tabs v-model="activeTab" grow>
        <v-tab value="Dashboard">
          Overview
        </v-tab>
        <v-tab value="Institutions">
          Institutions
        </v-tab>
        <v-tab v-if="isAdminOrRoot" value="Managers">Managers</v-tab>
      </v-tabs>
    </v-container>

    <v-container v-if="tenant" class="py-4 ma-0 pa-0" fluid>

      <v-row v-if="activeTab === 'Institutions'">
        <v-col cols="12">
          <institutions-data-table :show-filters="false" :tenant="tenant"/>
        </v-col>
      </v-row>

      <v-row v-if="activeTab === 'Dashboard'">
        <v-col cols="9" class="text-center">
          <h1>Insights coming soon</h1>
        </v-col>
        <v-col cols="3">
          <tenant-details :tenant="tenant"/>
        </v-col>
      </v-row>

      <v-row v-if="activeTab === 'Managers'">
        <v-col cols="12">
          <managers-table v-if="isAdminOrRoot" @revoked="fetchTenant"
                          :managers="managers as User[]"
                          :entity-type="Entity.Tenant"/>
        </v-col>

      </v-row>
      <v-row>
        <v-col>

        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="showCreateInstitution" :persistent="true" max-width="500">
      <create-tenant-institution-cohort
        :entity-title="$t('components.CreateCards.CreateTenantInstitutionCohort.titleCreateInstitution')"
        :tenant="tenant"
        :entity-type="Entity.Institution"
        :allowed="store.hasAbility(Entity.Institution, Action.create) || isAdminOrRoot"
        data-pw="createInstitutionCard"
        @close="showCreateInstitution = false"
      />
    </v-dialog>

    <v-dialog v-model="showDisableEnableTenant" :persistent="true" max-width="500">
      <disable-enable-tenant-dialog
        v-if="tenant" @close="onDisableEnableDialog"
        :disabled="tenant ? tenant.disabled : false"
      />
    </v-dialog>

    <v-dialog
      v-model="showDisableEnableTenant"
      :persistent="true"
      max-width="500"
    >
      <disable-enable-tenant-dialog
        v-if="tenant"
        @close="onDisableEnableDialog"
        :disabled="tenant ? tenant.disabled : false"
      />
    </v-dialog>

    <v-dialog
      v-model="showDeleteTenant"
      :persistent="true"
      max-width="500"
    >
      <delete-tenant
        v-if="tenant"
        @close="showDeleteTenant = false"
        :title="$t('components.TenantDetailPage.delete_title')"
        :confirm-string="tenant ? tenant.title : ''"
        :message="$t('components.TenantDetailPage.warning')"
        :label="$t('components.TenantDetailPage.instruction')"
      />
    </v-dialog>
  </authenticated-layout>
</template>

<script setup lang="ts">
import AuthenticatedLayout from "@/layout/AuthenticatedLayout.vue";
import {computed, onMounted, ref, watch} from "vue";
import {Action, Entity, isYapiError, Role, Tenant, User, YapiError} from "@YenzaCT/sdk";
import {useRoute} from "vue-router";
import yapi from "@/lib/yapi";
import TenantDetails from "@/pages/Manage/TenantDetailPage/components/TenantDetails.vue";
import DisableEnableTenantDialog from "@/pages/Manage/TenantDetailPage/components/DisableEnableTenantDialog.vue";
import {useGlobalStore} from "@/store";
import DeleteTenant from "@/pages/Manage/TenantDetailPage/components/DeleteTenant.vue";
import ManagersTable from "@/components/ManagersTable.vue";
import CreateTenantInstitutionCohort from "@/components/CreateCards/CreateTenantInstitutionCohortCard.vue";
import InstitutionsDataTable from "@/components/DataTables/InstitutionsDataTable.vue";
import PageHeader from "@/components/PageHeader.vue";

const store = useGlobalStore();
const route = useRoute();

const tenant = ref<Tenant>();
const managers = ref<User[]>([]);
const showCreateInstitution = ref(false);
const showDisableEnableTenant = ref(false);
const showDeleteTenant = ref(false);
const isAdminOrRoot = ref(store.user?.app.role === Role.Admin || store.user?.app.role === Role.Root);
const activeTab = ref("Dashboard");

const breadcrumbItems = computed(() => [
  { text: "Tenants", to: { name: "tenants" } },
  { text: tenant.value?.title || "" }
]);

async function fetchTenant() {
  try {
    store.networkBusy = true;
    const tenantId = route.params.id as string;
    await yapi.admin.tenant.get(tenantId).then((response) => {
      tenant.value = response.data;
    });
    await yapi.admin.managers.list(Entity.Tenant, tenantId).then((response) => {
      managers.value = response.data;
    });
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    store.networkBusy = false;
  }
}

onMounted(async () => {
  await fetchTenant();
});

watch(() => route.params.id, async (newId, oldId) => {
  if (newId !== oldId) {
    await fetchTenant();
  }
});

async function onDisableEnableDialog() {
  showDisableEnableTenant.value = false;
  await fetchTenant();
}
</script>
<style>
.selectable {
  cursor: pointer;
}

.selectable:hover {
  background-color: #f5f5f5;

}
</style>
